import { useState, type FormEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Field, Input } from "../../components/ui";
import { cognitoConfig, confirmSignUp, resendCode, signIn } from "../../lib/cognito";
import { messageFor } from "../../lib/cognitoErrors";
import { trackSignUp } from "../../lib/analytics";
import { safeNext, signupPath, DEFAULT_NEXT } from "../../lib/nextParam";
import { store } from "../../lib/session";
import { AuthAlert, AuthCard, AuthLink, AuthNote, CodeField } from "./parts";

/**
 * Confirm the emailed code. Arriving from /signup the password rides along in
 * router state, so confirming signs the coach in; arriving from a failed sign-in
 * or a stale email link it ends at /login instead.
 */
export function Verify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const password = (useLocation().state as { password?: string } | null)?.password;

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const next = safeNext(params.get("next"));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    setNote(null);
    try {
      const cfg = await cognitoConfig();
      await confirmSignUp(cfg, email, code);
      // Here and not on the /signup form: until the code is confirmed the
      // account is one Cognito will never let anyone in with. The sign-in
      // below is part of signing up, so it deliberately does not also count
      // as a `login`.
      trackSignUp();
      if (password) {
        store(await signIn(cfg, email, password));
        navigate(next, { replace: true });
        return;
      }
      const login = new URLSearchParams({ verified: "1", email: email.trim() });
      if (next !== DEFAULT_NEXT) login.set("next", next);
      navigate(`/login?${login.toString()}`, { replace: true });
    } catch (err) {
      setError(messageFor(err).message);
    } finally {
      setPending(false);
    }
  };

  const onResend = async () => {
    setPending(true);
    setError(null);
    setNote(null);
    try {
      await resendCode(await cognitoConfig(), email);
      setNote("Code sent. It can take a minute to arrive — check your spam folder too.");
    } catch (err) {
      setError(messageFor(err).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthCard
      title="Check your email"
      subtitle={
        email ? (
          <>
            Enter the six-digit code we sent to <span className="font-medium text-slate-700">{email}</span>.
          </>
        ) : (
          "Enter your email and the six-digit code we sent you."
        )
      }
      footer={
        <>
          Wrong address? <AuthLink to={signupPath(next)}>Start over</AuthLink>
        </>
      }
    >
      {note ? <AuthNote>{note}</AuthNote> : null}
      {error ? <AuthAlert>{error}</AuthAlert> : null}

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        {email ? null : (
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </Field>
        )}

        <Field label="Verification code">
          <CodeField value={code} onChange={setCode} />
        </Field>

        <Button type="submit" size="lg" disabled={pending || code.length < 6} className="w-full">
          {pending ? "Verifying…" : "Verify email"}
        </Button>
      </form>

      <div className="text-sm">
        <button
          type="button"
          onClick={() => void onResend()}
          disabled={pending || !email}
          className="font-medium text-brandText underline-offset-2 transition-colors hover:text-brandText hover:underline disabled:opacity-50"
        >
          Send a new code
        </button>
      </div>
    </AuthCard>
  );
}
